/**
 * PRIMITIVES - Base Design Tokens (Static Values)
 *
 * This file is the SINGLE SOURCE OF TRUTH for all raw color hex values,
 * spacing, radius, shadows, and typography primitives.
 *
 * These values come directly from the Figma design system and are hardcoded.
 * They are consumed by aliases.ts which maps them to role-based names,
 * and then by the PrimeNG preset via '{reference}' syntax.
 *
 * Token Hierarchy:
 * Primitives (this file) → Aliases (aliases.ts) → PrimeNG Preset ('{reference}' syntax)
 */

// ============================================
// COLOR PALETTES
// ============================================

/** Rentsody Blue - Secondary/Neutral - Global core brand */
export const rentsodyBlue = {
  50:  '#e7e8ec',
  100: '#ced1d8',
  200: '#b5bac4',
  300: '#838b9c',
  400: '#6b7489',
  500: '#525d75',
  600: '#394661',
  700: '#212f4e',
  800: '#08183a',
  900: '#071634',
  950: '#06132e',
} as const;

/** Rentals Magenta - Primary - Brand hero color for Rentals */
export const rentalsMagenta = {
  50:  '#f9edf2',
  100: '#f3dce4',
  200: '#eccad7',
  300: '#e6b9c9',
  400: '#e0a7bc',
  500: '#da95af',
  600: '#cd7294',
  700: '#c76186',
  800: '#c14f79',
  900: '#ae476d',
  950: '#9a3f61',
} as const;

/** Green - Success states */
export const green = {
  50:  '#f3f7f4',
  100: '#d7e5dd',
  200: '#c2d8cd',
  300: '#aecbbc',
  400: '#9abeab',
  500: '#86b29a',
  600: '#72a589',
  700: '#5d9879',
  800: '#357e57',
  900: '#30714e',
  950: '#2a6546',
} as const;

/** Red - Danger/Error states */
export const red = {
  50:  '#fbe6e8',
  100: '#e1b3b7',
  200: '#d79a9f',
  300: '#cd8187',
  400: '#c36870',
  500: '#b94f58',
  600: '#af3540',
  700: '#a51c28',
  800: '#9b0310',
  900: '#8b030e',
  950: '#7c020d',
} as const;

/** Yellow - Warning states */
export const yellow = {
  50:  '#fef4cd',
  100: '#dad0b4',
  200: '#cec19b',
  300: '#c2b181',
  400: '#b6a268',
  500: '#aa934f',
  600: '#9d8336',
  700: '#91741d',
  800: '#856404',
  900: '#785a04',
  950: '#6a5003',
} as const;

/** Home Aquamarine - Sub-brand: Home product */
export const homeAquamarine = {
  50:  '#e7f3f3',
  100: '#b6d9db',
  200: '#9ecdcf',
  300: '#86c0c3',
  400: '#6eb3b7',
  500: '#56a7ab',
  600: '#3d9a9f',
  700: '#258e93',
  800: '#0d8187',
  900: '#0c747a',
  950: '#0a676c',
} as const;

/** Lock Orange - Sub-brand: Lock product */
export const lockOrange = {
  50:  '#f8eee7',
  100: '#e9cbb6',
  200: '#e2b99e',
  300: '#dba886',
  400: '#d3976e',
  500: '#cc8556',
  600: '#c5743d',
  700: '#bd6225',
  800: '#b6510d',
  900: '#a4490c',
  950: '#92410a',
} as const;

/** SSR Blue - Primary color for the SSR Guest Portal */
export const ssrBlue = {
  50:  '#e8e8eb',
  100: '#b8b8c1',
  200: '#9f9fac',
  300: '#888898',
  400: '#707083',
  500: '#58586f',
  600: '#404059',
  700: '#282845',
  800: '#101030',
  900: '#0e0e2b',
  950: '#0d0d26',
} as const;

/** Guest Purple - Sub-brand: Guest product */
export const guestPurple = {
  50:  '#eee5f6',
  100: '#cbb2e5',
  200: '#ba99dd',
  300: '#a980d5',
  400: '#9766cc',
  500: '#864dc3',
  600: '#7533bb',
  700: '#631ab3',
  800: '#5200aa',
  900: '#4a0099',
  950: '#420088',
} as const;

/** Neutral palette - Based on Rentsody Blue values */
export const neutral = {
  white: '#ffffff',
  50:  '#e7e8ec',
  100: '#ced1d8',
  200: '#b5bac4',
  300: '#838b9c',
  400: '#6b7489',
  500: '#525d75',
  600: '#394661',
  700: '#212f4e',
  800: '#08183a',
  900: '#071634',
  950: '#06132e',
} as const;

// ============================================
// SPACING (4px grid system)
// ============================================

export const spacing = {
  4:  '0.25rem',   // 4px
  8:  '0.5rem',    // 8px
  16: '1rem',      // 16px
  24: '1.5rem',    // 24px
  32: '2rem',      // 32px
  40: '2.5rem',    // 40px
  48: '3rem',      // 48px
  56: '3.5rem',    // 56px
  64: '4rem',      // 64px
} as const;

// ============================================
// BORDER RADIUS
// ============================================

export const borderRadius = {
  4:    '0.25rem',  // 4px
  8:    '0.5rem',   // 8px
  16:   '1rem',     // 16px
  24:   '1.5rem',   // 24px
} as const;

// ============================================
// SHADOWS
// ============================================

export const shadows = {
  sm:   '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md:   '0 2px 4px 0 rgba(0, 0, 0, 0.25)',
  lg:   '0 3px 6px rgba(167, 185, 217, 0.65)',
  card: '0 2px 8px 0 rgba(0, 0, 0, 0.08)',
} as const;

// ============================================
// TYPOGRAPHY
// ============================================

export const fontFamily = {
  base: "'Poppins', system-ui, -apple-system, sans-serif",
} as const;

export const fontSize = {
  xs:   '0.75rem',    // 12px
  sm:   '0.875rem',   // 14px
  base: '1rem',       // 16px
  lg:   '1.125rem',   // 18px
  xl:   '1.25rem',    // 20px
  '2xl': '1.5rem',    // 24px
  '3xl': '2rem',      // 32px
  '4xl': '2.5rem',    // 40px
  '5xl': '3rem',      // 48px
} as const;

export const fontWeight = {
  normal:   '400',
  semibold: '600',
  bold:     '700',
} as const;

export const lineHeight = {
  base: '1rem',       // 16px
  lg:   '1.25rem',    // 20px
  xl:   '1.5rem',     // 24px
  '2xl': '1.75rem',   // 28px
  '3xl': '2rem',      // 32px
  '4xl': '2.5rem',    // 40px
  '5xl': '3rem',      // 48px
} as const;


